import React, { useMemo, useRef, useState } from 'react';
import { Box, Menu, MenuItem, Stack, Tooltip } from '@mui/material';
import { useDaily, useParticipantIds } from '@daily-co/daily-react-hooks';
import { IPastLessonsButtonProps } from './types';
import { TrayButton } from '../../shared/components/Tray/Tray';
import { useScreenShare } from '../../shared/contexts/ScreenShareProvider';
import { getPastLessons, getStudents } from '../pastLessons';
import { theme } from '../../../../../common/styles/theme';
import { DateUtil } from '../../../../../common/utils/dateUtil';
import { TimeUtils } from '../../../../../common/utils/time-utils';
import { RecentIcon } from '../../../../../../icons/RecentIcon';

const shortDate = (iso: string) =>
	new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

// Teacher only: the newest recordings with the students in the room. Picking one opens it in this
// tab (the call shrinks to the preview) and shares the tab right away.
export function PastLessonsButton({
	groups,
	roomId,
	myId,
	isTeacher,
	onOpen,
}: IPastLessonsButtonProps) {
	const callObject = useDaily();
	const remoteIds = useParticipantIds({ filter: 'remote' });
	const { shareCurrentTab } = useScreenShare();
	const anchorRef = useRef<HTMLDivElement | null>(null);
	const [open, setOpen] = useState(false);

	const students = useMemo(() => {
		const all = (callObject?.participants() || {}) as Record<string, any>;
		return getStudents(remoteIds.map(id => all[id]).filter(Boolean), myId);
	}, [callObject, remoteIds.join(','), myId]);

	const lessons = useMemo(
		() => getPastLessons(groups, students.map(s => s.id), roomId),
		[groups, students, roomId],
	);

	if (!isTeacher) return null;

	const names = students.map(s => s.name).join(', ');
	const noStudents = students.length === 0;

	const onPick = (groupId: number, audioId: number) => {
		setOpen(false);
		onOpen(groupId, audioId);
		shareCurrentTab(); // not awaited: getDisplayMedia must stay inside the click gesture
	};

	return (
		<>
			<Tooltip title={noStudents ? 'Waiting for a student' : ''}>
				<Box ref={anchorRef} component={'span'} sx={{ display: 'inline-flex' }}>
					<TrayButton
						className="tray-button--past-lessons"
						label="Past lessons"
						disabled={noStudents}
						onClick={() => setOpen(true)}
					>
						<RecentIcon />
					</TrayButton>
				</Box>
			</Tooltip>

			<Menu
				open={open}
				anchorEl={anchorRef.current}
				onClose={() => setOpen(false)}
				anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
				transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
				sx={{ '.MuiPaper-root': { width: '360px', mt: -1 } }}
			>
				<Box
					sx={{
						px: 2,
						py: 1,
						fontWeight: 600,
						color: theme.palette.grey[700],
						borderBottom: `1px solid ${theme.palette.grey[100]}`,
					}}
				>
					Lessons with {names}
				</Box>

				{lessons.length === 0 &&
					<MenuItem disabled>No past lessons with {names} yet</MenuItem>
				}
				{lessons.map(({ groupId, audio }) => (
					<MenuItem
						key={audio.id}
						onClick={() => onPick(groupId, audio.id)}
					>
						<Stack direction={'row'} alignItems={'baseline'} gap={1.5} sx={{ width: 1, minWidth: 0 }}>
							<Box sx={{ flex: '0 0 auto', width: '56px', color: theme.palette.grey[500], fontVariantNumeric: 'tabular-nums' }}>
								{shortDate(audio.createdAt)}
							</Box>
							<Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
								{audio.title || DateUtil.formatDateTime(new Date(audio.createdAt))}
							</Box>
							{!!audio.duration &&
								<Box sx={{ flex: '0 0 auto', color: theme.palette.grey[400] }}>
									{TimeUtils.secondsToString(audio.duration)}
								</Box>
							}
						</Stack>
					</MenuItem>
				))}

				<Box
					sx={{
						px: 2,
						py: 1,
						fontSize: theme.typography.caption.fontSize,
						color: theme.palette.grey[500],
						borderTop: `1px solid ${theme.palette.grey[100]}`,
					}}
				>
					Opens the lesson in this tab and shares it with {names}
				</Box>
			</Menu>
		</>
	);
}
